import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Modal, ModalBody } from 'react-bootstrap';
import {
    DndContext,
    useSensor,
    useSensors,
    DragEndEvent,
    DragMoveEvent,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    DragOverlay
} from '@dnd-kit/core';
import { useDispatch, useSelector } from 'react-redux';
import StudioMenu from 'Components/Studio/SideMenu/SideMenu'
import { AppDispatch, RootState } from 'Vendor/Store/store';
import { GRID_COLUMNS, MOBILE_GRID_COLUMNS } from 'Vendor/Utils/Constants';
import { GridItemPosition, GridItemSize, ActiveDragData } from 'Vendor/Utils/StudioTypes';
import GridCanvas from 'Components/Studio/Canvas/GridCanvas';
import GridItem from 'Components/Studio/Canvas/Items/_GridItem';
import TopControls from 'Components/Studio/Canvas/TopControls';
import BottomControls from 'Components/Studio/Canvas/BottomControls';
import { generateTempUUID } from 'Vendor/Store/studio/initialState';
import { itemAdded, itemMoved, itemResized, itemSelected, setCreationData } from 'Vendor/Store/studio';
import Creation from 'Entity/Creation';
import { UserContext } from 'Vendor/Context/UserContextProvider';
import useAutoSaveCreation from 'Hooks/useAutoSaveCreation';
import DragOverlayContent from './DragOverlayContent';
import CreateItemFromDrag from 'Vendor/Utils/CreateItemFromDrag';

interface CreationModalType {
    show: boolean
    setShow: (show: boolean) => void
    creation: Creation
    onSaved?: () => void
}

const CreationModal = ({ creation, show, setShow, onSaved }: CreationModalType) => {
    const { currentUser } = useContext(UserContext);

    const dispatch = useDispatch<AppDispatch>();
    const responsiveMode = useSelector((state: RootState) => state.studio.present.responsiveMode);
    const zoomLevel = useSelector((state: RootState) => state.studio.present.zoomLevel);
    const activePageId = useSelector((state: RootState) => state.studio.present.activePageId);

    const [activeDrag, setActiveDrag] = useState<ActiveDragData | null>(null);
    const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
    const [resizePreview, setResizePreview] = useState<{ position: GridItemPosition; size: GridItemSize } | null>(null);
    const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const creationId = creation?.id;

    const { forceSave } = useAutoSaveCreation({ creationId, enabled: show });

    const closeStudioModal = useCallback(async () => {
        try {
            await forceSave();
        } catch (err) {
            console.error('Failed to save creation before closing studio:', err);
            return;
        }

        // Notify parent that we saved; parent may refresh its creation prop
        try {
            onSaved?.();
        } catch (e) {
            // ignore
        }

        setShow(false);
    }, [forceSave, setShow, onSaved]);

    useEffect(() => {
        if (creation && show) {
            dispatch(setCreationData(creation));
        }
    }, [creation, show, dispatch, currentUser]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: () => ({ x: 0, y: 0 }),
        })
    );

    useEffect(() => {
        const handlePointerMove = (e: PointerEvent) => {
            lastPointerRef.current = { x: e.clientX, y: e.clientY };
        };

        window.addEventListener('pointermove', handlePointerMove);

        return () => window.removeEventListener('pointermove', handlePointerMove);
    }, []);


    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveDrag(active.data.current as ActiveDragData);

        if (active.data.current?.type === 'move' && activePageId) {
            dispatch(itemSelected({ itemId: active.id as string, pageId: activePageId }));
        }
    };

    const handleDragMove = (event: DragMoveEvent) => {
        const { active, delta, over } = event;
        const activeData = active.data.current as ActiveDragData;

        if (activeData?.type !== 'resize' && (!over || over.id !== 'grid-canvas')) {
            if (resizePreview) setResizePreview(null);
            return;
        }

        const gridCanvas = document.getElementById('grid-canvas');
        if (!gridCanvas) return;

        const activeGridColumns = responsiveMode === 'mobile' ? MOBILE_GRID_COLUMNS : GRID_COLUMNS;
        const rect = gridCanvas.getBoundingClientRect();
        const columnWidth = rect.width / activeGridColumns;
        const rowHeight = parseFloat(getComputedStyle(gridCanvas).gridAutoRows);

        if (activeData?.type === 'resize' && activeData.item && activeData.handle) {
            const item = activeData.item;
            const handle = activeData.handle;
            const currentPos = item.position;
            const currentSize = item.size;
            const col_delta = Math.round(delta.x / (columnWidth * zoomLevel));
            const row_delta = Math.round(delta.y / (rowHeight * zoomLevel));
            let newPosition = { ...currentPos };
            let newSize = { ...currentSize };

            if (handle.includes('left')) {
                const spanChange = -col_delta;
                const newColSpan = Math.max(1, Math.min(currentSize.col_span + spanChange, currentPos.col_start + currentSize.col_span - 1));
                newPosition.col_start = currentPos.col_start + currentSize.col_span - newColSpan;
                newSize.col_span = newColSpan;
            }
            if (handle.includes('right') || handle === 'middle-right') {
                newSize.col_span = Math.max(1, Math.min(currentSize.col_span + col_delta, activeGridColumns - currentPos.col_start + 1));
            }
            if (handle.includes('top')) {
                const spanChange = -row_delta;
                const newRowSpan = Math.max(1, currentSize.row_span + spanChange);
                newPosition.row_start = currentPos.row_start + currentSize.row_span - newRowSpan;
                newSize.row_span = newRowSpan;
            }
            if (handle.includes('bottom') || handle === 'bottom-center') {
                newSize.row_span = Math.max(1, currentSize.row_span + row_delta);
            }

            setResizePreview({ position: newPosition, size: newSize });
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over, delta } = event;
        const activeData = active.data.current as ActiveDragData;

        setActiveDrag(null);
        setResizePreview(null);

        if (!activePageId) {
            console.error("No active page found to modify");
            return;
        }

        if (!activeData || (!over && activeData.type !== 'move' && activeData.type !== 'resize')) {
            return;
        }

        const gridCanvas = document.getElementById('grid-canvas');

        if (!gridCanvas) return;

        const activeGridColumns = responsiveMode === 'mobile' ? MOBILE_GRID_COLUMNS : GRID_COLUMNS;
        const rowHeight = parseFloat(getComputedStyle(gridCanvas).gridAutoRows);
        const columnWidth = gridCanvas.offsetWidth / activeGridColumns; // Use untransformed width for calculation

        if (activeData?.type === 'new-item' || activeData.type === 'existing-image') {
            const gridRect = gridCanvas.getBoundingClientRect();
            const pointer = lastPointerRef.current;
            if (!pointer) return;

            const mouseXInGrid = pointer.x - gridRect.left;
            const mouseYInGrid = pointer.y - gridRect.top;

            const dropX = mouseXInGrid / zoomLevel;
            const dropY = mouseYInGrid / zoomLevel;

            const isNewImage =
                activeData.type === 'new-item' &&
                (activeData.itemType === 'PRODUCT' || activeData.itemType === 'FILE' || activeData.itemType === 'EXTERNAL_IMAGE');
            const isExistingImage = activeData.type === 'existing-image';
            const isImageType = isNewImage || isExistingImage;

            const size = isImageType ? { col_span: 4, row_span: 4 } : { col_span: 2, row_span: 2 };
            const col_start = Math.max(
                1,
                Math.min(activeGridColumns - size.col_span + 1, Math.floor(dropX / columnWidth) + 1)
            );
            const row_start = Math.max(1, Math.floor(dropY / rowHeight) + 1);

            const { asset: newAsset, desktopLayout, mobileLayout } = CreateItemFromDrag({
                activeData,
                col_start,
                row_start,
                size,
                currentStyle: active.data.current?.style
            })

            const assetId = generateTempUUID();
            const instanceId = generateTempUUID();

            dispatch(
                itemAdded({
                    pageId: activePageId,
                    asset: newAsset,
                    desktopLayout,
                    mobileLayout,
                    assetId,
                    instanceId,
                })
            );

            dispatch(itemSelected({ itemId: instanceId, pageId: activePageId }));
        } else if (activeData?.type === 'move' && activeData.item) {
            const item = activeData.item;
            const currentPos = item.position;
            const currentSize = item.size;

            const col_delta = Math.round(delta.x / (columnWidth * zoomLevel));
            const row_delta = Math.round(delta.y / (rowHeight * zoomLevel));

            const newPosition: GridItemPosition = {
                col_start: Math.max(1, Math.min(activeGridColumns - currentSize.col_span + 1, currentPos.col_start + col_delta)),
                row_start: Math.max(1, currentPos.row_start + row_delta),
            };
            dispatch(itemMoved({ pageId: activePageId, itemId: item.id, newPosition }));

        } else if (activeData?.type === 'resize' && activeData.item && activeData.handle) {
            const item = activeData.item;
            const handle = activeData.handle;
            const currentPos = item.position;
            const currentSize = item.size;
            const col_delta = Math.round(delta.x / (columnWidth * zoomLevel));
            const row_delta = Math.round(delta.y / (rowHeight * zoomLevel));
            let newPosition = { ...currentPos };
            let newSize = { ...currentSize };

            if (handle.includes('left')) {
                const spanChange = -col_delta;
                const newColSpan = Math.max(1, Math.min(currentSize.col_span + spanChange, currentPos.col_start + currentSize.col_span - 1));
                newPosition.col_start = currentPos.col_start + currentSize.col_span - newColSpan;
                newSize.col_span = newColSpan;
            }
            if (handle.includes('right') || handle === 'middle-right') {
                newSize.col_span = Math.max(1, Math.min(currentSize.col_span + col_delta, activeGridColumns - currentPos.col_start + 1));
            }
            if (handle.includes('top')) {
                const spanChange = -row_delta;
                const newRowSpan = Math.max(1, currentSize.row_span + spanChange);
                newPosition.row_start = currentPos.row_start + currentSize.row_span - newRowSpan;
                newSize.row_span = newRowSpan;
            }
            if (handle.includes('bottom') || handle === 'bottom-center') {
                newSize.row_span = Math.max(1, currentSize.row_span + row_delta);
            }

            dispatch(itemResized({ pageId: activePageId, itemId: item.id, newPosition, newSize }));
        }
    };

    const getRecentColors = () => {
        const pages = creation?.data?.pages || {};

        const colors = Object.values(pages)
            .flatMap((page: any) => Object.values(page.layouts || {}))
            .flatMap((layout: any) => Object.values(layout.items || {}))
            .flatMap((item: any) =>
                Object.entries(item.styles || {})
                    .filter(([key, value]) =>
                        typeof value === "string" &&
                        (key.toLowerCase().includes("color") ||
                        value.startsWith("#") ||
                        value.startsWith("rgb") ||
                        value.startsWith("hsl"))
                    )
                    .map(([name, value]) => ({ name, value: String(value) }))
            );

        const uniqueColors = Array.from(
            new Map(colors.map(c => [c.value, c])).values()
        );

        return uniqueColors;
    };

    return (
        <Modal fullscreen show={show} onHide={closeStudioModal} id="studio-modal">
            <DndContext onDragStart={handleDragStart} onDragMove={handleDragMove} onDragEnd={handleDragEnd} sensors={sensors}>
                <ModalBody className="layout">
                    <StudioMenu
                        creationId={creationId as number}
                        title={creation.name}
                        setShow={setShow}
                        forceSave={forceSave}
                        recentUsedColors={getRecentColors()}
                    />
                    <div className="h-100 w-100 bg-body position-relative">
                        <TopControls creationId={creationId as number} forceSave={forceSave} />
                        <BottomControls />
                        <div ref={containerRef} className="h-100 w-100 overflow-auto">
                            <GridCanvas
                                resizePreview={resizePreview}
                                canvasOffset={canvasOffset}
                                setCanvasOffset={setCanvasOffset}
                            />
                        </div>
                        <DragOverlay style={{ zIndex: 9999 }} adjustScale={false}>
                            {(activeDrag?.type === 'new-item' || activeDrag?.type === 'existing-image') && (
                                <div className="p-1 bg-light rounded shadow-lg drag-overlay-card">
                                    <button className="btn btn-ghost w-100 justify-content-start text-start p-2 drag-overlay-button">
                                        <DragOverlayContent
                                            itemType={activeDrag.itemType}
                                            colorValue={activeDrag.colorValue}
                                            imageUrl={activeDrag.imageUrl}
                                        />
                                    </button>
                                </div>
                            )}
                            {activeDrag?.type === 'move' && activeDrag.item && (
                                <GridItem item={activeDrag.item} isSelected={false} />
                            )}
                        </DragOverlay>
                    </div>
                </ModalBody>
            </DndContext>
        </Modal>
    );
};

export default CreationModal;
